import * as React from "react"
import { ConsumerBillType } from "@/types"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import type { DateRange } from "react-day-picker"

import { BillingOverviewType, WeeklyPaymentsChartType } from "@/types/"
import { meterDailyAnalyticsCountType } from "@/types/meter"
import { meterChartConfig } from "@/config/charts"
import { canViewThisPage } from "@/lib/api/auth"
import {
  getBillingOverview,
  getWeeklyPayments,
  sendEmailReminder,
} from "@/lib/api/billing"
import {
  getBillingDailyCount,
  getConsumerBillPieAnalytics,
} from "@/lib/api/consumers"
import { getMeterDailyAnalyticsCount } from "@/lib/api/meter"
import { getStatusStyle } from "@/lib/statusStyles"
import { toast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { BarChartStacked } from "@/components/charts/barchart-stacked"
import { PieChartComponent } from "@/components/charts/piechart"
import { DateRangePicker } from "@/components/date-range-picker"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"
import SelectTimeRange, {
  timeRangeOptions,
} from "@/components/select-time-range"
import { BillingTable } from "@/components/table/billing-table"

export const Route = createFileRoute("/_home/billing")({
  beforeLoad: () => {
    if (canViewThisPage("himo_billing.view_consumerbilling")) {
      return
    }

    return redirect({ to: "/access-denied" })
  },
  component: GenerateBillPage,
})

function GenerateBillPage() {
  // State to store selected rows
  const [selectedRows, setSelectedRows] = React.useState<ConsumerBillType[]>([])
  const [activeTab, setActiveTab] = React.useState("All")
  const [timeRange, setTimeRange] = React.useState(timeRangeOptions()[0])
  const [date, setDate] = React.useState<DateRange>({
    from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
    to: new Date(), // Today
  })
  const [meterAnalyticsDate, setMeterAnalyticsDate] = React.useState<DateRange>(
    {
      from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
      to: new Date(), // Today
    },
  )
  const [consumerBillAnalyticsDate, setConsumerBillAnalyticsDate] =
    React.useState<DateRange>({
      from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
      to: new Date(), // Today
    })

  const [billingDailyCountDate, setBillingDailyCountDate] =
    React.useState<DateRange>({
      from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
      to: new Date(), // Today
    })

  const {
    data: meterDailyAnalyticsCount,
    isRefetching: meterDailyAnalyticsCountLoading,
  } = useQuery<meterDailyAnalyticsCountType>({
    queryKey: [
      "meterDailyAnalyticsCount",
      meterAnalyticsDate.from?.toISOString(),
      meterAnalyticsDate.to?.toISOString(),
    ],
    queryFn: async () => {
      const from = meterAnalyticsDate.from ?? new Date()
      const to = meterAnalyticsDate.to ?? new Date()
      return await getMeterDailyAnalyticsCount(
        from.toISOString(),
        to.toISOString(),
      )
    },
  })

  const {
    data: consumerBillAnalytics,
    isLoading: isConsumerBillAnalyticsLoading,
  } = useQuery({
    queryKey: [
      "consumerBillAnalytics",
      consumerBillAnalyticsDate.from?.toISOString(),
      consumerBillAnalyticsDate.to?.toISOString(),
    ],
    queryFn: async () => {
      const from = consumerBillAnalyticsDate.from ?? new Date()
      const to = consumerBillAnalyticsDate.to ?? new Date()
      return await getConsumerBillPieAnalytics(
        from.toISOString(),
        to.toISOString(),
      )
    },
  })

  const {
    data: billingDailyCountData,
    isLoading: isBillingDailyCountDataLoading,
  } = useQuery({
    queryKey: [
      "billingDailyCountData",
      billingDailyCountDate.from?.toISOString(),
      billingDailyCountDate.to?.toISOString(),
    ],
    queryFn: async () => {
      const from = billingDailyCountDate.from ?? new Date()
      const to = billingDailyCountDate.to ?? new Date()
      return await getBillingDailyCount(from.toISOString(), to.toISOString())
    },
  })

  // Function to update selected rows
  const handleSelectedRowsChange = React.useCallback(
    (rows: ConsumerBillType[]) => {
      setSelectedRows(rows)
    },
    [],
  )

  // 👉 Call API to send a reminder
  const handleSendReminder = async (reminderType: string) => {
    const billingIds = selectedRows.map((row) => Number(row.billing_id))
    /*  console.log(
      `Sending ${reminderType.replace("_", " ")} reminder to:`,
      billingIds,
    ) */

    try {
      await sendEmailReminder(billingIds, reminderType)
      toast({
        title: "Reminder Sent!",
        description: `Successfully sent ${reminderType}`,
      })
    } catch (error: any) {
      //console.error("Error sending reminder:", error)
      toast({
        title: "Something went wrong!",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  // to replace by billing overview api
  const { data: billingOverview, isLoading: isBillingOverviewLoading } =
    useQuery<BillingOverviewType>({
      queryKey: ["billingOverview", timeRange.value],
      queryFn: () => getBillingOverview(Number(timeRange.value)),
    })

  const { data: weeklyPayments } = useQuery<WeeklyPaymentsChartType[]>({
    queryKey: ["weeklyPayments", date],
    queryFn: () => getWeeklyPayments(date),
  })

  const renderOverviewCard = (
    title: string,
    icon: keyof typeof Icons,
    data?: {
      total: number | string
      percentage: number | null
      status: string
    },
  ) => {
    const IconComponent = Icons[icon]
    const {
      color,
      bgColor,
      icon: StatusIcon,
    } = getStatusStyle(data?.status ?? "", data?.percentage ?? null)

    //console.log(data)
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex gap-2 text-[13px] font-medium text-muted-foreground">
            {["Total Consumers", "Active Consumers"].includes(title) && (
              <Icons.person className="h-[18px] w-[18px]" />
            )}
            {[
              "Total Payments",
              "Total Billing",
              "Total Bill Adjustment",
            ].includes(title) && (
              <img
                src="peso-sign.png"
                alt="icon"
                className="h-[18px] w-[18px]"
              />
            )}
            {["Total Meters"].includes(title) && (
              <Icons.gauge className="h-[20px] w-[20px] text-[#00B6AE]" />
            )}
            {["Before Due"].includes(title) && (
              <Icons.circle className="h-[20px] w-[20px] rounded-full bg-yellow-500" />
            )}
            {["Due Date"].includes(title) && (
              <Icons.circle className="h-[20px] w-[20px] rounded-full bg-primary" />
            )}
            {["Over Due"].includes(title) && (
              <Icons.circle className="h-[20px] w-[20px] rounded-full bg-orange-500" />
            )}
            {["Disconnection"].includes(title) && (
              <Icons.circle className="h-[20px] w-[20px] rounded-full bg-red-600" />
            )}
            <p className="line-height-[14px] font-worksans font-bold tracking-wider">
              {title}
            </p>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-2xl font-bold">
          {isBillingOverviewLoading ? (
            <Skeleton className="h-8 w-24" />
          ) : data?.total !== undefined ? (
            <div className="flex flex-row items-center gap-2">
              <h1 className="font-semibold">
                {title === "Total Payments" ? `PHP ${data.total}` : data.total}
              </h1>
              {data.status !== "New" && (
                <span
                  className={`rounded-sm px-1 text-[13px] font-normal ${color} ${bgColor}`}
                >
                  {data.status !== "Flat" && data.percentage !== null
                    ? `${data.percentage.toFixed(2)}%`
                    : ""}

                  {StatusIcon && (
                    <span
                      className={
                        data.status === "Flat"
                          ? "flex items-center"
                          : "inline-block px-1"
                      }
                    >
                      {StatusIcon}
                    </span>
                  )}
                </span>
              )}
            </div>
          ) : (
            "N/A"
          )}
        </CardContent>
      </Card>
    )
  }
  return (
    <main className="space-y-4 p-4">
      {/* 👉 Update Breadcrumb based on active tab */}
      <div className="flex w-full justify-between pt-4">
        <div>
          <Header subroutes={[{ route: "/billing", label: "Billing" }]}>
            {"Billing(" + activeTab + ")"}
          </Header>
        </div>
        <div className="flex items-center gap-3">
          <Label>Set view</Label>
          <SelectTimeRange range={timeRange} setRange={setTimeRange} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3 md:items-stretch">
        <div className="grid-cols-5 gap-4 space-y-4 overflow-visible font-worksans md:col-span-4 md:grid md:space-y-0">
          {renderOverviewCard(
            "Total Consumer Bill",
            "peso",
            billingOverview?.total_consumer_bill,
          )}
          {renderOverviewCard(
            "Before Due Reminder Bill",
            "peso",
            billingOverview?.before_due_reminder_bill,
          )}
          {renderOverviewCard(
            "Due Date Reminder Bill",
            "user",
            billingOverview?.due_date_reminder_bill,
          )}
          {renderOverviewCard(
            "Over Due Reminder Bill",
            "user",
            billingOverview?.overdue_reminder_bill,
          )}
          {renderOverviewCard(
            "Disconnection Reminder Bill",
            "user",
            billingOverview?.disconnection_reminder_bill,
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-3">
        <Card className="col-span-2">
          <CardHeader className="flex flex-col items-center justify-between py-2 lg:flex-row">
            <CardTitle>Billing Analytics</CardTitle>
            <div>
              <DateRangePicker
                date={billingDailyCountDate}
                setDate={setBillingDailyCountDate}
              />
            </div>
          </CardHeader>
          <CardContent>
            {/* Show loading when refetching */}
            {isBillingDailyCountDataLoading ? (
              <div className="flex h-[240px] items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            ) : billingDailyCountData ? (
              <BarChartStacked data={billingDailyCountData} />
            ) : (
              <div className="flex h-[240px] items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-col items-center justify-between py-2 lg:flex-row">
            <CardTitle>Billing Statistics</CardTitle>
            <DateRangePicker
              date={consumerBillAnalyticsDate}
              setDate={setConsumerBillAnalyticsDate}
            />
          </CardHeader>
          <CardContent>
            {isConsumerBillAnalyticsLoading ? (
              <div className="flex h-[240px] items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            ) : consumerBillAnalytics &&
              consumerBillAnalytics?.consumer_bill_distribution ? (
              <div className="flex h-full items-center justify-center">
                <PieChartComponent
                  data={consumerBillAnalytics?.consumer_bill_distribution ?? []}
                  dataKey="count"
                  nameKey="consumer_bill_status"
                  className="height:1000px"
                />
              </div>
            ) : (
              <div className="flex h-[240px] items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <div className="flex h-full flex-col">
        <Tabs
          defaultValue="All"
          className="h-full w-full"
          onValueChange={(value) => {
            const formattedValue = value
              .replace("-", " ")
              .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize first letter
            setActiveTab(formattedValue)
          }}
        >
          {/* Scrollable container with padding for better visibility */}
          <div className="overflow-x-auto p-0">
            <TabsList className="m-0 flex h-12 w-max items-center overflow-hidden rounded-lg border-2 bg-white p-0">
              <TabsTrigger
                value="All"
                className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
              >
                All Billing
              </TabsTrigger>
              <TabsTrigger
                value="before-due"
                className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
              >
                Before Due
              </TabsTrigger>
              <TabsTrigger
                value="due-date"
                className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
              >
                Due Date
              </TabsTrigger>
              <TabsTrigger
                value="overdue"
                className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
              >
                Overdue
              </TabsTrigger>
              <TabsTrigger
                value="disconnection"
                className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
              >
                Disconnection
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Billing Tabs */}
          <TabsContent value="All">
            <BillingTable
              status="all"
              urlEndpoint="consumer-bill?"
              date={date}
              setDate={setDate}
              selectedRows={selectedRows}
              onSelectedRowsChange={handleSelectedRowsChange}
              onSendReminder={() => handleSendReminder("all")}
            />
          </TabsContent>
          <TabsContent value="before-due">
            <BillingTable
              status="before_due"
              urlEndpoint="consumer-bill?reminder_type=before_due_reminder&"
              date={date}
              setDate={setDate}
              selectedRows={selectedRows}
              onSelectedRowsChange={handleSelectedRowsChange}
              onSendReminder={() => handleSendReminder("before_due_reminder")}
            />
          </TabsContent>
          <TabsContent value="due-date">
            <BillingTable
              status="due_date"
              urlEndpoint="consumer-bill?reminder_type=due_date_today_reminder&"
              date={date}
              setDate={setDate}
              selectedRows={selectedRows}
              onSelectedRowsChange={handleSelectedRowsChange}
              onSendReminder={() =>
                handleSendReminder("due_date_today_reminder")
              }
            />
          </TabsContent>
          <TabsContent value="overdue">
            <BillingTable
              status="overdue"
              urlEndpoint="consumer-bill?reminder_type=overdue_reminder&"
              date={date}
              setDate={setDate}
              selectedRows={selectedRows}
              onSelectedRowsChange={handleSelectedRowsChange}
              onSendReminder={() => handleSendReminder("overdue_reminder")}
            />
          </TabsContent>
          <TabsContent value="disconnection">
            <BillingTable
              status="disconnection"
              urlEndpoint="consumer-bill?reminder_type=disconnection_reminder&"
              date={date}
              setDate={setDate}
              selectedRows={selectedRows}
              onSelectedRowsChange={handleSelectedRowsChange}
              onSendReminder={() =>
                handleSendReminder("disconnection_reminder")
              }
            />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  )
}
